import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BaseComponent } from 'src/app/common/base/base.component';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AbsiTemplateTypeResource, AbsiTemplateVersionResource } from '../../absi';
import { FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import moment from 'moment';
import { Observable, Subscription } from 'rxjs';
import { AbsiTemplateVersionRefresh } from '../../store/absi-template/absi-template.action';
import { MatSort } from '@angular/material/sort';
import { FormType } from 'src/app/common/enumerations/enumerations';

@Component({
  selector: 'absi-builder-side',
  templateUrl: './absi-builder-side.component.html',
  styleUrls: ['./absi-builder-side.component.scss']
})
export class AbsiBuilderSideComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {

  @Input() filter?: string;
  @Input() version?: AbsiTemplateVersionResource | null;

  @Output() selectedRow = new EventEmitter<AbsiTemplateVersionResource>();
  @Output() selectedType = new EventEmitter<AbsiTemplateTypeResource>();

  versions!: AbsiTemplateVersionResource[];
  versions$!: Observable<AbsiTemplateVersionResource[]>;
  versionsSubs!: Subscription;

  absiTemplateTypes?: AbsiTemplateTypeResource[];
  absiTemplateTypes$!: Observable<AbsiTemplateTypeResource[]>;
  absiTemplateTypesSubs!: Subscription;

  type?: AbsiTemplateTypeResource;
  typeCtrl = new FormControl();

  public displayedColumns: string[] = ['name', 'statusName'];

  dataSource!: MatTableDataSource<AbsiTemplateVersionResource>;
  @ViewChild(MatSort) sort!: MatSort;

  ngOnInit(): void {
    this.loadAbsiTemplateTypes();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.loadVersions();
  }

  override ngOnDestroy(): void {
    this.absiTemplateTypesSubs?.unsubscribe();
    this.versionsSubs?.unsubscribe();
    super.ngOnDestroy();
  }

  /**
   * Loads template types and restores the type from the open ABSIT document when
   * the user returns to the builder. Falls back to the default ABSI type.
   */
  loadAbsiTemplateTypes() {
    this.absiTemplateTypes$ = this.store.select(state => state.AbsiTemplateType.data);
    this.absiTemplateTypesSubs = this.absiTemplateTypes$.subscribe(async data => {
      if (data?.length) {
        this.absiTemplateTypes = data;
        if (data.length == 1) {
          this.type = data[0];
          this.typeCtrl.setValue(this.type);
          this.getVersions();
        }

        const document = await this.redirect.getDocument(FormType.ABSIT);
        const type = this.absiTemplateTypes.find(t => t.id == document?.gen1);
        if (document && this.type?.id != type?.id) {
          this.type = type;
          this.typeCtrl.setValue(this.type);
          this.getVersions();
        } else if (!document && !this.type) {
          const defaultType = this.absiTemplateTypes.find(t => t.id == 6);
          if (defaultType) {
            this.type = defaultType;
            this.typeCtrl.setValue(this.type);
            this.getVersions();
          }
        }
      }
    });
  }

  /**
   * Broadcasts the selected type to the parent and refreshes the slim version
   * list for the side panel.
   */
  getVersions() {
    this.selectedType.emit(this.type);
    if (this.type?.id) {
      this.store.dispatch(new AbsiTemplateVersionRefresh(this.type.id));
      this.loadVersions();
    }
  }

  displayType(type: any) {
    return type ? type.name : null;
  }

  selected(event: MatAutocompleteSelectedEvent) {
    this.type = event.option.value;
    this.getVersions();
  }

  /**
   * Keeps the table in sync with the store and restores the open ABSIT document
   * without re-emitting the same selected row after refreshes.
   */
  loadVersions() {
    this.versionsSubs?.unsubscribe();
    this.versions$ = this.store.select(state => state.AbsiTemplateVersions.data);
    this.versionsSubs = this.versions$.subscribe(async data => {
      if (data) {
        this.versions = data.sort((a, b) => this.utils.sort(a.name, b.name, false));
        if (this.version) {
          this.version = this.versions.find(x => x.id == this.version?.id);
        }
        this.versions.map(v => this.setStatus(v));
        this.dataSource = new MatTableDataSource(this.versions);
        this.setSort<AbsiTemplateVersionResource>(this.dataSource, this.sort);

        const document = await this.redirect.getDocument(FormType.ABSIT);
        if (document) {
          const version = this.versions.find(v => v.id == document.formID);
          if (version?.id != this.version?.id)
            this.onRowClick(version);
        }
      }
    });
  }

  /**
   * Marks versions relative to the active template so the side list can show
   * Current, Archived, or New without loading the full workflow tree.
   */
  setStatus(e: AbsiTemplateVersionResource) {
    const activeVersion = this.versions.find(x => x.status == 1);
    if (!activeVersion) {
      e.statusClass = 'new-version';
      e.statusName = 'New';
      return;
    }

    if (e == activeVersion) {
      activeVersion.statusClass = 'current-version';
      activeVersion.statusName = 'Active';
    }

    if (moment(e.createdOn).isBefore(activeVersion?.createdOn)) {
      e.statusClass = 'old-version';
      e.statusName = 'Archived';
    }

    if (moment(e.createdOn).isAfter(activeVersion?.createdOn)) {
      e.statusClass = 'new-version';
      e.statusName = 'New';
    }
  }

  /** Emits the side-list selection so the parent can hydrate the full template. */
  onRowClick(row: any) {
    this.version = row;
    if (this.version) {
      this.selectedRow.emit(this.version);
    }
  }

}
